import React from "react";

import { NOT_RECORDED, STATE_COLORS, barWidth, hasValue } from "./reportFormat";

/**
 * The small vocabulary both reports are built from. Keeping these here rather
 * than inline means the printed page and the screen stay in step: every section
 * gets the same padding, the same rule under its title, and the same
 * `report-section` break-control class the print stylesheet keys on.
 */

/**
 * `tone` picks the surface rather than letting a caller append `bg-navy-900`
 * after `bg-white`: two competing Tailwind background utilities resolve by
 * stylesheet order, not by the order they appear in the class attribute, so an
 * "override" silently loses and the header prints white-on-white.
 */
const SECTION_TONE = {
  light: "border border-neutral-200 bg-white",
  dark: "border-none bg-navy-900 text-white",
};

export const Section = ({ title, meta, children, tone = "light", className = "", id }) => (
  <section
    id={id}
    className={`report-section mb-4 rounded-xl p-5 sm:p-6 ${SECTION_TONE[tone]} ${className}`}
  >
    {title && (
      <header
        className={`mb-4 flex items-baseline justify-between gap-3 border-b-2 pb-2.5 ${
          tone === "dark" ? "border-navy-700" : "border-peri-100"
        }`}
      >
        <h2
          className={`font-display text-2xl font-semibold tracking-wide ${
            tone === "dark" ? "text-white" : "text-navy-900"
          }`}
        >
          {title}
        </h2>
        {meta && (
          <p
            className={`text-right text-[11.5px] leading-snug ${
              tone === "dark" ? "text-peri-200" : "text-peri-300"
            }`}
          >
            {meta}
          </p>
        )}
      </header>
    )}
    {children}
  </section>
);

/** A headline figure. `note` carries the definition, because a number like
 *  "A:C 1.42" is meaningless to a club director without it. */
export const Kpi = ({ label, value, unit, note, color }) => (
  <div className="rounded-[10px] bg-paper p-3.5">
    <div className="min-h-[32px] text-[11.5px] leading-tight text-peri-300">{label}</div>
    <div className="font-display text-[34px] leading-[1.1] font-semibold" style={{ color: color || "#0d1e41" }}>
      {value}
      {unit && <span className="text-[13px] font-normal text-ink"> {unit}</span>}
    </div>
    {note && <div className="mt-1 text-[11px] leading-snug text-ink">{note}</div>}
  </div>
);

export const Pill = ({ children, color = STATE_COLORS.accent }) => (
  <span
    className="inline-block rounded-full px-2.5 py-0.5 font-display text-[15px] leading-normal text-white"
    style={{ background: color }}
  >
    {children}
  </span>
);

/**
 * Label / bar / value row — the workhorse of both reports.
 *
 * `marker` draws the group average as a tick on the same track, so an athlete's
 * bar is always read against its comparison rather than in isolation.
 */
export const MeterRow = ({ label, value, display, marker, color = "#4274d9", muted }) => (
  <div className="mb-1.5 grid grid-cols-[minmax(96px,130px)_1fr_auto] items-center gap-2.5 text-[12.5px]">
    <span className={muted ? "text-peri-300" : "text-navy-900"}>{label}</span>
    <div className="relative h-2.5 overflow-hidden rounded-[5px] bg-peri-100">
      <span className="block h-full rounded-[5px]" style={{ width: barWidth(value), background: color }} />
      {hasValue(marker) && (
        <span
          className="absolute top-[-2px] h-[14px] w-0.5 bg-navy-900"
          style={{ left: barWidth(marker) }}
          aria-hidden="true"
        />
      )}
    </div>
    <b className="min-w-[64px] text-right font-semibold text-navy-900">{display ?? NOT_RECORDED}</b>
  </div>
);

export const Legend = ({ items }) => (
  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] text-ink">
    {items.map((item) => (
      <span key={item.label} className="flex items-center gap-1.5">
        <i className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: item.color }} />
        {item.label}
        {item.count !== undefined && `: ${item.count}`}
      </span>
    ))}
  </div>
);

/** Explicit "we have no data" rather than a section of dashes. Sparse data is
 *  the normal case for a club that just started logging, and a report that
 *  says so is more useful than one that renders an empty grid. */
export const Empty = ({ children }) => (
  <p className="py-3 text-[13px] text-ink">{children}</p>
);

/** Legal / interpretive banner. These reports mix training data with
 *  self-reported wellbeing, and the distinction has to travel with the page —
 *  including into the PDF, so it is never `no-print`. */
export const Notice = ({ children }) => (
  <div className="mb-4 rounded-r-lg border-l-4 border-navy-500 bg-peri-100 px-4 py-2.5 text-[12.5px] leading-relaxed text-[#2b3a5c]">
    {children}
  </div>
);

export const TableScroll = ({ children, minWidth = 900 }) => (
  <div className="report-table-scroll -mx-2 overflow-x-auto px-2">
    <div style={{ minWidth }}>{children}</div>
  </div>
);

export const Th = ({ children, onClick, active, direction, align = "left" }) => {
  const label = typeof children === "string" ? children : undefined;
  return (
    <th
      scope="col"
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
      className={`whitespace-nowrap border-b border-neutral-200 px-2 pb-2 text-[11.5px] font-medium text-peri-300 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-label={label ? `Ordenar por ${label}` : undefined}
          className="inline-flex items-center gap-1 rounded-sm hover:text-navy-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-200"
        >
          {children}
          {active && <span aria-hidden="true">{direction === "asc" ? "▲" : "▼"}</span>}
        </button>
      ) : (
        children
      )}
    </th>
  );
};

export const Td = ({ children, align = "left", className = "", ...rest }) => (
  <td
    className={`whitespace-nowrap border-b border-[#f0f2f7] px-2 py-2 ${
      align === "right" ? "text-right" : ""
    } ${className}`}
    {...rest}
  >
    {children}
  </td>
);
